import React from 'react';
import { HeartHandshakeIcon, UsersRoundIcon, SparklesIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { ButtonExternal, ButtonLink } from '../components/ui/Button';
import { DoodleUnderline } from '../components/brand/Doodles';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { externalLinks } from '../data/site';

// Giving links out to the PayPal destination supplied by BCM leadership, used
// exactly as provided. No payment form is ever rendered on this site.
//
// TODO: The supplied PayPal URL carries a session-style `token` parameter that
// could not be click-verified from the editor. Confirm it resolves for a real
// visitor, or have BCM supply a permanent PayPal donation URL / button code
// before launch.

const pillars = [{
  title: 'Bring In',
  copy: 'Welcome students and help them encounter the Gospel in a genuine Christian community.'
}, {
  title: 'Build Up',
  copy: 'Help students grow in faith, friendship, service, and leadership.'
}, {
  title: 'Send Out',
  copy: 'Equip students to live out their faith and serve on campus and beyond.'
}];
const otherWays = [{
  icon: UsersRoundIcon,
  title: 'Serve with us',
  copy: 'Students help lead worship, welcome new people, run outreach on campus, and serve at events throughout the year.'
}, {
  icon: SparklesIcon,
  title: 'Bring a friend',
  copy: 'One of the most valuable things anyone can do is invite a student who is looking for community to come on a Thursday.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Partner with BCM',
  copy: 'Churches and individuals who want to talk about partnering with BCM can reach the team directly by email.'
}];
export function Give() {
  return <>
      <PageHeader eyebrow="Give" title="Support ministry to college students" intro="UCF BCM is supported by churches and individuals who invest in Christian community and ministry among college students." data-id="element-503" data-mp="m6vbqxy1mbty" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="giving-heading" data-id="element-504" data-mp="3fw8tdfh5sass">
        <DoodleScatter tone="white" seed="give-paypal" data-id="element-505" data-mp="3jzeyukwg5xy7" />
        <Container className="relative grid items-start gap-8 lg:grid-cols-[1fr_0.85fr] lg:gap-12" data-id="element-506" data-mp="3oxyn4ryijltq">
          <div className="rounded-[24px] border-2 border-brand-navy bg-brand-blue p-7 sm:p-9" data-id="element-507" data-mp="2s88vlvlfjk4p">
            <h2 id="giving-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-[1.9rem]" data-id="element-508" data-mp="2w8j8ls7n3at2">
              Give to UCF BCM
            </h2>
            <DoodleUnderline className="mt-3 h-3 w-36" color="var(--bcm-dark-blue)" data-id="element-509" data-mp="35zw1mn56c70o" />
            <p className="mt-5 font-sans text-[16px] leading-relaxed text-ink" data-id="element-510" data-mp="28k8fxcc3khfv">
              Gifts are handled securely by PayPal. The button below opens BCM’s PayPal giving page in
              a new tab — no payment information is ever entered on this website.
            </p>
            <ButtonExternal href={externalLinks.paypalDonate} className="mt-7 min-h-[56px] px-8 py-4 text-[17px] sm:text-lg" data-id="element-511" data-mp="2107daexgux3p">
              Give Through PayPal
            </ButtonExternal>
            <p className="mt-4 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-512" data-mp="1ry7vhkemnozp">
              Questions about giving? Email the BCM team and someone will help.
            </p>
            <div className="mt-4" data-id="element-513" data-mp="1i8xzvukkro6u">
              <ButtonExternal href={externalLinks.email} variant="secondary" data-id="element-514" data-mp="1i7vc3viju1v5">
                Email About Giving
              </ButtonExternal>
            </div>
          </div>

          <div data-id="element-515" data-mp="18cbhdcfq197z">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-516" data-mp="18clb9b3w00iy">
              What your support makes possible
            </h2>
            <ul className="mt-6 space-y-5" data-id="element-517" data-mp="zalpy6luaaa7">
              {pillars.map(pillar => <li key={pillar.title} className="border-l-4 border-brand-yellow pl-5" data-id="element-518" data-mp="zcy6cfby8cl5">
                  <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-519" data-mp="2zey0h87jzoc4">{pillar.title}</h3>
                  <p className="mt-1.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-520" data-mp="33gj8af58dcfg">
                    {pillar.copy}
                  </p>
                </li>)}
            </ul>
          </div>
        </Container>
      </Section>

      <Section className="bcm-paper py-14 sm:py-18" labelledBy="other-ways-heading" data-id="element-521" data-mp="2fokw7sxyjd19">
        <Container data-id="element-522" data-mp="2knp6yo0j5itp">
          <h2 id="other-ways-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl" data-id="element-523" data-mp="2ko76pumeif6d">
            Other ways to support BCM
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-524" data-mp="2bnivt8sb0uxp">
            {otherWays.map(way => <li key={way.title} className="rounded-[22px] border-2 border-brand-navy bg-white p-6" data-id="element-525" data-mp="4xkoqerixtak">
                <way.icon aria-hidden="true" className="h-6 w-6 text-brand-navy" data-id="element-526" data-mp="9v4di8lulogr" />
                <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-navy" data-id="element-527" data-mp="k0ce1hrdziw2">
                  {way.title}
                </h3>
                <p className="mt-2 font-sans text-[15px] leading-relaxed text-ink" data-id="element-528" data-mp="srw7bod990jn">{way.copy}</p>
              </li>)}
          </ul>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-529" data-mp="o0mkevj3cbbg">
            <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-530" data-mp="12pj09ere2smm">Get Connected</ButtonExternal>
            <ButtonLink to="/about" variant="secondary" data-id="element-531" data-mp="xs0prjxq45ci">
              Learn More About BCM
            </ButtonLink>
          </div>
        </Container>
      </Section>
    </>;
}